import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Input,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { Form, Formik, FormikHelpers } from "formik";
import gql from "graphql-tag";
import React from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";

import FormGroup from "../../../components/elements/formGroup";
import FolderIcon from "../../../components/icons/folder";
import { GENERIC_SAVED_MESSAGE } from "../../../constants/lang/en";
import {
  FolderWithRelationsFragmentFragment,
  useFolderUpdateMutation,
} from "../../../graphql/graphql";
import { getRoutePath } from "../../../router";
import setServerErrors, {
  setGenericMessage,
} from "../../../utils/serverErrors";
import {
  descriptionSchema,
  nameSchema,
  yupObject,
} from "../../../utils/validation";

interface FolderUpdatePresenterProps {
  folderId: string;
  folder: FolderWithRelationsFragmentFragment;
  hasTargetFolder?: boolean;
}

const FolderUpdatePresenter: React.FC<FolderUpdatePresenterProps> = ({
  folderId,
  folder,
  hasTargetFolder,
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  const { folderId: folderIdFromUrl } = useParams();

  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const [FolderUpdateMutation, { loading: updateLoading }] =
    useFolderUpdateMutation();

  const onSubmit = async (
    data: FolderUpdateFormData,
    { setFieldError }: FormikHelpers<FolderUpdateFormData>
  ) => {
    try {
      const { errors } = await FolderUpdateMutation({
        variables: { id: folderId, data: { ...data } },
      });
      if (errors) {
        setServerErrors(errors, setFieldError);
      } else {
        onDrawerClose();
        toast({
          description: GENERIC_SAVED_MESSAGE,
          status: "success",
          position: "top",
          isClosable: true,
        });
        return;
      }
    } catch (error) {
      toast({
        description: setGenericMessage(error),
        status: "error",
        position: "top",
        isClosable: true,
      });
    }
  };

  const onDrawerClose = () => {
    onClose();
    setTimeout(() => {
      if (hasTargetFolder) {
        navigate(getRoutePath("foldersShow", { folderId: folderIdFromUrl }));
      } else {
        navigate(getRoutePath("foldersShow", { folderId }));
      }
    }, 500);
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onDrawerClose}>
      <Helmet>
        <title>Edit {folder.name} Folder</title>
      </Helmet>
      <DrawerOverlay>
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            <FolderIcon width="6" height="auto" marginRight="2" />
            Edit {folder.name} Folder
          </DrawerHeader>
          <DrawerBody>
            <Formik
              initialValues={{
                name: folder.name,
                description: folder.description || "",
              }}
              validationSchema={FolderUpdateValidationSchema}
              onSubmit={onSubmit}
              enableReinitialize
            >
              {({ getFieldProps }) => (
                <Form noValidate>
                  <FormGroup label="Name" name="name">
                    <Input
                      autoComplete="off"
                      autoFocus
                      {...getFieldProps("name")}
                    />
                  </FormGroup>
                  <Box marginTop="6" textAlign="center">
                    <Button
                      width="full"
                      type="submit"
                      isLoading={updateLoading}
                      marginTop="10"
                    >
                      Save
                    </Button>
                  </Box>
                  <Box marginTop="4" textAlign="center">
                    <Button
                      type="button"
                      variant="link"
                      colorScheme="secondary"
                      onClick={onDrawerClose}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          </DrawerBody>
        </DrawerContent>
      </DrawerOverlay>
    </Drawer>
  );
};

export default FolderUpdatePresenter;

const FolderUpdateValidationSchema = yupObject().shape({
  name: nameSchema,
  description: descriptionSchema,
});

type FolderUpdateFormData = {
  name: string;
  description: string;
};

gql`
  mutation FolderUpdate($id: UUID!, $data: FolderUpdateInput!) {
    folderUpdate(id: $id, data: $data) {
      ...FolderWithRelationsFragment
    }
  }
`;
